// CPU (Worker) で動くフィルター。純 TS なので Node でテストできる。
// 入力は RGBA (ストレートアルファ)、出力は新しい配列。

import type { FilterParams } from './registry'

/** Huang のスライディングヒストグラムによるメディアン (半径 r、各チャンネル独立)。端はクランプ */
export function medianFilter(src: Uint8ClampedArray, w: number, h: number, r: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(src.length)
  if (r <= 0 || w === 0 || h === 0) {
    out.set(src)
    return out
  }
  const win = 2 * r + 1
  const half = (win * win) >> 1
  const hist = new Uint32Array(256 * 4)
  const clampX = (x: number) => (x < 0 ? 0 : x >= w ? w - 1 : x)
  const clampY = (y: number) => (y < 0 ? 0 : y >= h ? h - 1 : y)
  const addCol = (x: number, y0: number, sign: number) => {
    const cx = clampX(x)
    for (let dy = -r; dy <= r; dy++) {
      const i = (clampY(y0 + dy) * w + cx) * 4
      hist[src[i]!] = hist[src[i]!]! + sign
      hist[256 + src[i + 1]!] = hist[256 + src[i + 1]!]! + sign
      hist[512 + src[i + 2]!] = hist[512 + src[i + 2]!]! + sign
      hist[768 + src[i + 3]!] = hist[768 + src[i + 3]!]! + sign
    }
  }
  for (let y = 0; y < h; y++) {
    hist.fill(0)
    for (let x = -r; x <= r; x++) addCol(x, y, 1)
    for (let x = 0; x < w; x++) {
      if (x > 0) {
        addCol(x - r - 1, y, -1)
        addCol(x + r, y, 1)
      }
      const o = (y * w + x) * 4
      for (let c = 0; c < 4; c++) {
        let acc = 0
        const base = c * 256
        let v = 0
        for (; v < 256; v++) {
          acc += hist[base + v]!
          if (acc > half) break
        }
        out[o + c] = v
      }
    }
  }
  return out
}

/** ダスト & スクラッチ: メディアンとの差がしきい値を超える画素だけ置き換える */
export function dustAndScratches(src: Uint8ClampedArray, w: number, h: number, r: number, threshold: number): Uint8ClampedArray {
  const med = medianFilter(src, w, h, r)
  if (threshold <= 0) return med
  for (let i = 0; i < src.length; i += 4) {
    const d = Math.max(Math.abs(med[i]! - src[i]!), Math.abs(med[i + 1]! - src[i + 1]!), Math.abs(med[i + 2]! - src[i + 2]!))
    if (d <= threshold) {
      med[i] = src[i]!
      med[i + 1] = src[i + 1]!
      med[i + 2] = src[i + 2]!
      med[i + 3] = src[i + 3]!
    }
  }
  return med
}

/** ノイズを軽減: バイラテラルフィルター (強さ = 色の許容幅、半径固定 3..6) */
export function reduceNoise(src: Uint8ClampedArray, w: number, h: number, strength: number, preserveDetails: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(src.length)
  const r = Math.max(1, Math.min(6, Math.round(1 + strength / 2.5)))
  const sigmaS = r / 1.5
  // preserveDetails が高いほど色差に厳しい (小さな sigmaR)
  const sigmaR = Math.max(2, (strength * 4) * (1 - preserveDetails / 100 * 0.8))
  const spatial = new Float32Array((2 * r + 1) * (2 * r + 1))
  for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) spatial[(dy + r) * (2 * r + 1) + dx + r] = Math.exp(-(dx * dx + dy * dy) / (2 * sigmaS * sigmaS))
  const rangeLut = new Float32Array(256 * 3)
  for (let d = 0; d < rangeLut.length; d++) rangeLut[d] = Math.exp(-(d * d) / (2 * sigmaR * sigmaR))
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const o = (y * w + x) * 4
      const cr = src[o]!, cg = src[o + 1]!, cb = src[o + 2]!
      let sr = 0, sg = 0, sb = 0, sa = 0, sw = 0
      for (let dy = -r; dy <= r; dy++) {
        const yy = y + dy
        if (yy < 0 || yy >= h) continue
        for (let dx = -r; dx <= r; dx++) {
          const xx = x + dx
          if (xx < 0 || xx >= w) continue
          const i = (yy * w + xx) * 4
          const d = Math.abs(src[i]! - cr) + Math.abs(src[i + 1]! - cg) + Math.abs(src[i + 2]! - cb)
          const wgt = spatial[(dy + r) * (2 * r + 1) + dx + r]! * rangeLut[d]!
          sr += src[i]! * wgt
          sg += src[i + 1]! * wgt
          sb += src[i + 2]! * wgt
          sa += src[i + 3]! * wgt
          sw += wgt
        }
      }
      out[o] = sr / sw
      out[o + 1] = sg / sw
      out[o + 2] = sb / sw
      out[o + 3] = sa / sw
    }
  }
  return out
}

/** 油彩風: 桑原フィルター (4 象限のうち分散が最小の平均を採用)。積分画像で O(1) の窓計算 */
export function kuwahara(src: Uint8ClampedArray, w: number, h: number, r: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(src.length)
  if (r <= 0) {
    out.set(src)
    return out
  }
  const W = w + 1
  // 積分画像: 各チャンネルの和と、輝度の二乗和
  const sum = new Float64Array(W * (h + 1) * 4)
  const sq = new Float64Array(W * (h + 1))
  for (let y = 1; y <= h; y++) {
    let rowR = 0, rowG = 0, rowB = 0, rowA = 0, rowQ = 0
    for (let x = 1; x <= w; x++) {
      const i = ((y - 1) * w + (x - 1)) * 4
      const R = src[i]!, G = src[i + 1]!, B = src[i + 2]!, A = src[i + 3]!
      const lum = 0.299 * R + 0.587 * G + 0.114 * B
      rowR += R; rowG += G; rowB += B; rowA += A; rowQ += lum * lum
      const k = y * W + x, up = (y - 1) * W + x
      sum[k * 4] = sum[up * 4]! + rowR
      sum[k * 4 + 1] = sum[up * 4 + 1]! + rowG
      sum[k * 4 + 2] = sum[up * 4 + 2]! + rowB
      sum[k * 4 + 3] = sum[up * 4 + 3]! + rowA
      sq[k] = sq[up]! + rowQ
    }
  }
  const box = (x0: number, y0: number, x1: number, y1: number, res: Float64Array) => {
    // [x0,x1) × [y0,y1)、クランプ済み
    const a = y0 * W + x0, b = y0 * W + x1, c = y1 * W + x0, d = y1 * W + x1
    const n = (x1 - x0) * (y1 - y0)
    for (let ch = 0; ch < 4; ch++) res[ch] = (sum[d * 4 + ch]! - sum[b * 4 + ch]! - sum[c * 4 + ch]! + sum[a * 4 + ch]!) / n
    const lumMean = 0.299 * res[0]! + 0.587 * res[1]! + 0.114 * res[2]!
    const q = (sq[d]! - sq[b]! - sq[c]! + sq[a]!) / n
    res[4] = q - lumMean * lumMean
  }
  const tmp = new Float64Array(5)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let best = Infinity
      let br = 0, bg = 0, bb = 0, ba = 0
      for (let q = 0; q < 4; q++) {
        const x0 = q & 1 ? x : Math.max(0, x - r), x1 = q & 1 ? Math.min(w, x + r + 1) : x + 1
        const y0 = q & 2 ? y : Math.max(0, y - r), y1 = q & 2 ? Math.min(h, y + r + 1) : y + 1
        box(x0, y0, x1, y1, tmp)
        if (tmp[4]! < best) {
          best = tmp[4]!
          br = tmp[0]!; bg = tmp[1]!; bb = tmp[2]!; ba = tmp[3]!
        }
      }
      const o = (y * w + x) * 4
      out[o] = br; out[o + 1] = bg; out[o + 2] = bb; out[o + 3] = ba
    }
  }
  return out
}

const n = (p: FilterParams, k: string) => Number(p[k] ?? 0)

/** id で CPU フィルターを実行する (Worker とフォールバックの両方から呼ぶ) */
export function runCpuFilter(id: string, rgba: Uint8ClampedArray, w: number, h: number, p: FilterParams): Uint8ClampedArray {
  switch (id) {
    case 'median': return medianFilter(rgba, w, h, Math.round(n(p, 'radius')))
    case 'despeckle': return medianFilter(rgba, w, h, 1)
    case 'dustScratches': return dustAndScratches(rgba, w, h, Math.round(n(p, 'radius')), n(p, 'threshold'))
    case 'reduceNoise': return reduceNoise(rgba, w, h, n(p, 'strength'), n(p, 'preserveDetails'))
    case 'oilPaint': return kuwahara(rgba, w, h, Math.round(n(p, 'radius')))
    default: throw new Error(`未知の CPU フィルター: ${id}`)
  }
}
